import React, { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Button as SpectrumButton, ButtonGroup, Content, Dialog, DialogContainer, Divider, Heading, useDialogContainer, } from '@adobe/react-spectrum';
import Crop from '@spectrum-icons/workflow/Crop';
import { is } from 'app/core/markers/models';
import { getProjectState } from 'app/stores/project';
import { cropImageMarker } from 'app/stores/project/marker';
import { getImage, getMarker } from 'app/stores/selectors';
import { useAppDispatch } from 'app/stores/store';
import { Button } from 'app/views/components/editor/sidebar/Button';
import { Image } from 'app/views/components/svg/Image';
import { SVG } from 'app/views/components/svg/SVG';
import { CropView } from '../../platform/desktop/workspace/imageCrop/CropView';
const ImageView = (props) => {
    const { id, viewBox } = props;
    const image = useSelector((state) => getImage(state, id));
    if (!image) {
        return null;
    }
    return (React.createElement(SVG, { id: "image-crop", width: "100%", height: "100%", preserveAspectRatio: "none", viewBox: viewBox
            ? `${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}`
            : undefined },
        React.createElement("g", { id: "image" },
            React.createElement(Image, { src: image.url, width: image.size.width, height: image.size.height }))));
};
const CropDialog = (props) => {
    const { markerId } = props;
    const dialog = useDialogContainer();
    const dispatch = useAppDispatch();
    const crop = useRef(undefined);
    const getImageSelector = React.useCallback((state) => {
        const marker = getMarker(state, markerId);
        if (!is(marker, 'Image')) {
            return undefined;
        }
        const image = getImage(state, marker.image);
        if (!image) {
            return undefined;
        }
        return {
            id: image.id,
            crop: marker.crop,
            width: image.size.width,
            height: image.size.height,
        };
    }, [markerId]);
    const onCrop = React.useCallback(() => {
        if (crop.current) {
            dispatch(cropImageMarker({
                id: markerId,
                crop: crop.current,
            }));
        }
        dialog.dismiss();
    }, [crop, markerId, dialog, dispatch]);
    const onChange = useCallback((rect) => {
        crop.current = Object.assign({}, rect);
    }, [crop]);
    return (React.createElement(Dialog, null,
        React.createElement(Heading, { level: 6 }, "Crop image"),
        React.createElement(Divider, null),
        React.createElement(Content, null,
            React.createElement(CropView, { cacheStateKey: "image-crop-unified", itemSelector: getImageSelector, Content: ImageView, onChange: onChange })),
        React.createElement(ButtonGroup, null,
            React.createElement(SpectrumButton, { variant: "secondary", onPress: dialog.dismiss }, "Cancel"),
            React.createElement(SpectrumButton, { variant: "accent", onPress: onCrop }, "Crop"))));
};
export const ImagePicker = () => {
    const selection = useSelector((state) => getProjectState(state).selection);
    const [isOpen, setOpen] = React.useState(false);
    if (selection.length !== 1) {
        return null;
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(Button, { "data-test-id": "crop-image", label: "Crop", Icon: Crop, onPress: () => setOpen(true) }),
        React.createElement(DialogContainer, { type: "fullscreen", onDismiss: () => setOpen(false) }, isOpen && React.createElement(CropDialog, { markerId: selection[0] }))));
};
